// /quotes/new and /quotes/:id: the quote builder. Agent dealers quote list price
// and can discount to the floor; below it the quote needs Phoneware's approval and
// says why. Wholesale dealers see their buy price and margin. Phoneware sees a
// dealer's quote read only, with the approval decision for Billing ops and Super
// admins when it is waiting on them. A Super admin edits a new quote, the drafts they
// started for a dealer's customer, or the house dealer's; Sales at Phoneware quotes
// the house dealer's customers only (mock M1). A quote can start from a template
// (?from=) and be saved as one.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TAX_NOTE_PHASE_1,
  canSend,
  floorViolations,
  formatCents,
  formatTerm,
  quoteTotals,
  roleLabel,
  type Billing,
  type CatalogProductV2,
  type ContractTerm,
  type CustomerContact,
  type CustomerSummary,
  type Dealer,
  type DealerModel,
  type PricedProduct,
  type Quote,
  type QuoteInput,
  type QuoteLine,
  type QuoteTemplateDetail,
  type QuoteType,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { companyTypeName } from "../api-customers.js";
import { pricedForDealer, startLines } from "../api-templates.js";
import { Icon } from "../icons.js";
import { Link, navigate } from "../router.js";
import { isNotYours, useLoad, useSession } from "../session.js";
import { BILLING, QUOTE_TYPE, TERMS, centsToDollars, errorText, isEmail, parseDollars, plural, whenIn } from "../format.js";
import { Callout, Chip, Crumb, DecidedBy, Head, NotYours, QtyBox, QuoteStatusChip, Sheet, ViewOnly, useTitle, useToast } from "../ui.js";
import { SaveAsTemplateSheet, StartedFromNotice, type StartedFrom } from "./Templates.js";
import { MadeBy, MadeByCallout } from "./connect-bits.js";
import { IntakeCreatedNotice } from "./intake-bits.js";
import { RecordActivity } from "./Activity.js";
import { QuoteLinePicker, type PickerSource } from "./quote-picker.js";

/** The types a quote can be made as here. A MAC (change) quote starts only from New MAC order with its source ticket (plan 7C). */
const PICKER_TYPES: QuoteType[] = ["new", "renewal"];

interface Draft {
  customer: { id: number; name: string } | null;
  contactName: string | null;
  contactEmail: string | null;
  type: QuoteType;
  term: ContractTerm;
  lines: QuoteLine[];
  pdfNotes: string;
  internalNotes: string;
  approvalNote: string;
}

const EMPTY: Draft = {
  customer: null,
  contactName: null,
  contactEmail: null,
  type: "new",
  term: 36,
  lines: [],
  pdfNotes: "",
  internalNotes: "",
  approvalNote: "",
};

function draftOf(q: Quote): Draft {
  return {
    customer: { id: q.customerId, name: q.customerName },
    contactName: q.contactName,
    contactEmail: q.contactEmail,
    type: q.type,
    term: q.term,
    lines: q.lines,
    pdfNotes: q.pdfNotes,
    internalNotes: q.internalNotes,
    approvalNote: q.approval.requestNote ?? "",
  };
}

/** The signer Omni suggests from the customer's live Autotask contacts: the primary contact, else the first with an email. */
function signerOf(contacts: CustomerContact[]): CustomerContact | null {
  return contacts.find((x) => x.roles.includes("primary") && x.email) ?? contacts.find((x) => x.email) ?? null;
}

/**
 * The QuoteLink a customer opens: on Omni's public base from GET /api/config (the origin the server puts on every email
 * link, LENS_BASE_URL), not whatever address this page was opened on.
 */
export function useQuoteLinkUrl(): (token: string) => string | null {
  const { api } = useSession();
  const config = useLoad(() => api.config(), []);
  const continueUrl = config.data?.emailLinkContinueUrl ?? "";
  const base = URL.canParse(continueUrl) ? new URL(continueUrl).origin : null;
  return (token) => (base ? `${base}/q/${encodeURIComponent(token)}` : null);
}

// ------------------------------------------------------------------ pieces

/** A price in dollars, kept as cents. */
function PriceBox({ cents, onCents, label, testId, tone }: { cents: number; onCents: (c: number) => void; label: string; testId: string; tone: string }): React.ReactElement {
  const [text, setText] = useState(centsToDollars(cents));
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setText(centsToDollars(cents));
  }, [cents]);
  return (
    <label className={tone ? `pricebox ${tone}` : "pricebox"}>
      <span className="sr">{label}</span>
      <span className="cur">$</span>
      <input
        className="input"
        inputMode="decimal"
        data-testid={testId}
        value={text}
        onFocus={() => {
          editing.current = true;
        }}
        onChange={(e) => {
          setText(e.target.value);
          const next = parseDollars(e.target.value);
          if (next !== null) onCents(next);
        }}
        onBlur={() => {
          editing.current = false;
          setText(centsToDollars(cents));
        }}
      />
    </label>
  );
}

function guide(line: QuoteLine, model: DealerModel, ops: boolean): React.ReactNode {
  const list = formatCents(line.listPrice);
  if (ops) {
    return [`List ${list}`, line.floorPrice !== null ? `floor ${formatCents(line.floorPrice)}` : null, line.buyPrice !== null ? `buy ${formatCents(line.buyPrice)}` : null]
      .filter(Boolean)
      .join(" \u00b7 ");
  }
  if (model === "wholesale") {
    if (line.buyPrice === null) return `Your price ${list}`;
    const margin = line.unitPrice - line.buyPrice;
    const pct = line.unitPrice > 0 ? Math.round((margin / line.unitPrice) * 100) : 0;
    return (
      <span className={margin < 0 ? "g-bad" : undefined}>
        Buy {formatCents(line.buyPrice)} · margin {formatCents(margin)} ({pct}%)
      </span>
    );
  }
  if (line.floorPrice === null) return `List ${list}`;
  if (line.unitPrice < line.floorPrice) {
    return (
      <span className="g-low">
        <Icon name="triangle-alert" size={12} />
        Below the floor of {formatCents(line.floorPrice)}. Needs approval.
      </span>
    );
  }
  if (line.unitPrice === line.floorPrice) {
    return (
      <span className="g-floor">
        <Icon name="triangle-alert" size={12} />
        At the floor. Any lower needs approval.
      </span>
    );
  }
  const off = line.listPrice > 0 ? Math.round((1 - line.unitPrice / line.listPrice) * 100) : 0;
  return `List ${list} \u00b7 floor ${formatCents(line.floorPrice)}${off > 0 ? ` \u00b7 ${off}% off list` : ""}`;
}

function tone(line: QuoteLine, model: DealerModel): string {
  if (model !== "agent" || line.floorPrice === null) return "";
  if (line.unitPrice < line.floorPrice) return "low";
  return line.unitPrice === line.floorPrice ? "floor" : "";
}

function Lines({
  billing,
  lines,
  model,
  editable,
  ops,
  onLine,
  onRemove,
}: {
  billing: Billing;
  lines: QuoteLine[];
  model: DealerModel;
  editable: boolean;
  ops: boolean;
  onLine: (productId: string, patch: Partial<Pick<QuoteLine, "qty" | "unitPrice">>) => void;
  onRemove: (productId: string) => void;
}): React.ReactElement {
  const mine = lines.filter((l) => l.billing === billing);
  const head = BILLING[billing];
  return (
    <section className="card lines">
      <div className="card-h">
        <h2>{head}</h2>
        <span className="note">{billing === "monthly" ? "Billed every month in OneBill" : "Billed once, due at signing"}</span>
      </div>
      {mine.length ? (
        <div className="tablewrap">
          <table className={editable ? "t t-lines" : "t t-lines ro"}>
            <colgroup>
              <col className="c-prod" />
              <col className="c-qty" />
              <col className="c-price" />
              <col className="c-total" />
              {editable ? <col className="c-x" /> : null}
            </colgroup>
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Unit price</th>
                <th className="num">Line total</th>
                {editable ? (
                  <th>
                    <span className="sr">Remove</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {mine.map((l) => (
                <tr key={l.productId}>
                  <td className="l-prod">
                    <b>{l.name}</b>
                    <span className="code block">{[l.code, l.unit].filter(Boolean).join(" \u00b7 ")}</span>
                  </td>
                  <td className="l-qty">
                    {editable ? (
                      <QtyBox qty={l.qty} onQty={(qty) => onLine(l.productId, { qty })} label={`Quantity of ${l.name}`} testId={`quote-line-qty-${l.productId}`} />
                    ) : (
                      l.qty
                    )}
                  </td>
                  <td className="l-price">
                    {editable ? (
                      <PriceBox
                        cents={l.unitPrice}
                        onCents={(c) => onLine(l.productId, { unitPrice: c })}
                        label={`Unit price for ${l.name}`}
                        testId={`quote-line-price-${l.productId}`}
                        tone={tone(l, model)}
                      />
                    ) : (
                      <span className="num">{formatCents(l.unitPrice)}</span>
                    )}
                    <span className="guide">{guide(l, model, ops)}</span>
                  </td>
                  <td className="num l-total">{formatCents(l.qty * l.unitPrice)}</td>
                  {editable ? (
                    <td className="l-x">
                      <button className="iconbtn" type="button" aria-label={`Remove ${l.name}`} onClick={() => onRemove(l.productId)}>
                        <Icon name="x" size={15} />
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty">No {head.toLowerCase()} lines yet.{editable ? " Add them from the catalog below." : ""}</div>
      )}
    </section>
  );
}

function CustomerPicker({
  chosen,
  onChoose,
  dealerId,
}: {
  chosen: { id: number; name: string } | null;
  onChoose: (c: CustomerSummary) => void;
  /** Phoneware quotes for one dealer's customer at a time; "" while the house dealer is still loading. */
  dealerId: string | null;
}): React.ReactElement {
  const { api } = useSession();
  const [text, setText] = useState(chosen?.name ?? "");
  const [open, setOpen] = useState(false);
  // Every customer the caller sees, as Omni stored them at the last sync: one read, searched here, no Autotask call.
  const [all, setAll] = useState<CustomerSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chosen) setText(chosen.name);
  }, [chosen]);

  useEffect(() => {
    if (!open || all !== null) return;
    let live = true;
    api.customers().then(
      (list) => {
        if (!live) return;
        setAll([...list].sort((a, b) => a.name.localeCompare(b.name)));
        setError(null);
      },
      (err: unknown) => {
        if (live) setError(errorText(err));
      },
    );
    return () => {
      live = false;
    };
  }, [api, open, all]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && e.target instanceof Node && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const q = text.trim().toLowerCase();
  const found = all === null ? null : all.filter((c) => (dealerId === null || c.dealerId === dealerId) && (!q || c.name.toLowerCase().includes(q)));

  return (
    <div className="combo" ref={box}>
      <label className="search">
        <Icon name="search" size={15} />
        <span className="sr">Find a customer</span>
        <input
          className="input"
          type="search"
          autoComplete="off"
          placeholder="Find a customer by name"
          data-testid="quote-customer-search"
          value={text}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
        />
      </label>
      {open ? (
        <ul className="combo-list" role="listbox" aria-label="Customers">
          {error ? (
            <li className="empty">{error}</li>
          ) : found === null ? (
            <li className="empty">Looking</li>
          ) : found.length === 0 ? (
            <li className="empty">
              No customer matches. <Link to="/customers">Add one on Customers</Link>.
            </li>
          ) : (
            found.slice(0, 8).map((c) => (
              <li key={c.autotaskCompanyId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={chosen?.id === c.autotaskCompanyId}
                  data-testid={`quote-customer-option-${c.autotaskCompanyId}`}
                  onClick={() => {
                    onChoose(c);
                    setText(c.name);
                    setOpen(false);
                  }}
                >
                  <b>{c.name}</b>
                  <span>{[c.address ? `${c.address.city}, ${c.address.state}` : null, c.companyType === 1 ? null : companyTypeName(c.companyType)].filter(Boolean).join(" \u00b7 ")}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

function SendSheet({ quote, dealerName, onSent, onClose }: { quote: Quote; dealerName: string; onSent: (q: Quote) => void; onClose: () => void }): React.ReactElement {
  const { api } = useSession();
  const quoteLinkUrl = useQuoteLinkUrl();
  const [to, setTo] = useState(quote.link?.sentTo ?? quote.contactEmail ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<Quote | null>(null);
  const valid = isEmail(to);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const next = await api.sendQuote(quote.id, { to: to.trim() });
      setSent(next);
      onSent(next);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent?.link) {
    const url = quoteLinkUrl(sent.link.token) ?? "";
    return (
      <Sheet
        title="QuoteLink sent"
        onClose={onClose}
        foot={
          <>
            <button className="btn" type="button" onClick={onClose}>
              Back to the quote
            </button>
            <a className="btn primary" href={url} target="_blank" rel="noreferrer">
              <Icon name="smartphone" size={17} />
              Open what the customer sees
            </a>
          </>
        }
      >
        <div className="sent-ok">
          <span className="ok-ic">
            <Icon name="check" size={22} />
          </span>
          <h3>Sent to {sent.link.sentTo}</h3>
          <p>You will see it on Today when they open it and when they sign.</p>
          <div className="linkbox">
            <code>{url}</code>
          </div>
        </div>
      </Sheet>
    );
  }
  return (
    <Sheet
      title={quote.link ? "Send the QuoteLink again" : "Send the QuoteLink"}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" disabled={!valid || busy} data-testid="quote-send-confirm" onClick={() => void send()}>
            <Icon name="send" size={15} />
            {busy ? "Sending" : "Send"}
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="field">
          <span>To</span>
          <input className="input" type="email" autoComplete="email" data-testid="quote-send-to" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <p className="card-p">
          Omni emails a link to read and sign {quote.number} on a phone or a computer. It opens in {dealerName}&rsquo;s brand.
        </p>
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

function PdfSheet({ quote, onClose }: { quote: Quote; onClose: () => void }): React.ReactElement {
  const { api } = useSession();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    api.quotePdf(quote.id).then(
      (blob) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      },
      (err: unknown) => {
        if (live) setError(errorText(err));
      },
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [api, quote.id]);
  return (
    <Sheet
      title={`PDF, ${quote.number}`}
      wide
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
          {url ? (
            <a className="btn primary" href={url} download={`${quote.number}.pdf`}>
              <Icon name="file-down" size={17} />
              Download PDF
            </a>
          ) : null}
        </>
      }
    >
      <div className="pdf-stage">
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : url ? (
          <iframe title={`${quote.number} as a PDF`} src={url} />
        ) : (
          <div className="empty">Making the PDF.</div>
        )}
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ the screen

export function QuoteBuilder({ id, customerId, templateId }: { id: string | null; customerId: number | null; templateId: string | null }): React.ReactElement {
  const { me, api, can, everyDealer, writeScope, canWrite, seesPrices } = useSession();
  const toast = useToast();
  const quoteLinkUrl = useQuoteLinkUrl();
  // Phoneware sees every dealer's quotes, so it reads the selling dealer for its name and model, and the whole catalog.
  const ops = everyDealer;
  const quoteScope = writeScope("quotes.write");
  const loaded = useLoad<Quote | null>(() => (id ? api.quote(id) : Promise.resolve(null)), [id]);
  const quote = loaded.data;
  const dealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  const template = useLoad<QuoteTemplateDetail | null>(() => (id === null && templateId ? api.quoteTemplate(templateId) : Promise.resolve(null)), [id, templateId]);
  const [draftDealerId, setDraftDealerId] = useState<string | null>(null);
  const dealerId = quote?.dealerId ?? draftDealerId ?? template.data?.dealerId ?? null;
  const dealer = ops ? dealers.data?.find((d) => d.id === dealerId) ?? null : me.dealer;
  const isHouse = Boolean(dealer?.house);
  const house = dealers.data?.find((d) => d.house) ?? null;
  // Writes quotes for this dealer: a dealer for its own, Sales at Phoneware for the house dealer, a Super admin for any.
  const writesHere = canWrite("quotes.write", dealer) && (!ops || isHouse);
  // A dealer edits its drafts. A Super admin edits a new quote, drafts they started, or drafts on the house dealer; Sales at Phoneware, the house
  // dealer's. A MAC quote is only made and sold from New MAC order (plan 7C), so the builder shows it and never changes it.
  const mac = quote?.type === "change";
  const editable =
    seesPrices &&
    !mac &&
    (quote
      ? (quote.status === "draft" || quote.status === "needs_approval") && canWrite("quotes.write", dealer) && (!ops || isHouse || quote.repEmail === me.user.email)
      : id === null && quoteScope !== null);
  // A Phoneware tier reading a quote it can't change, and why (mock M1).
  const viewOnly = ops && quote !== null && !mac && !canWrite("quotes.write", dealer);
  const catalog = useLoad<PricedProduct[]>(() => (can("catalog.read_all") ? Promise.resolve([]) : api.pricedCatalog()), [ops]);
  // Phoneware is sent the whole catalog record; quoting, it sees it the way the customer's dealer does.
  const fullCatalog = useLoad<CatalogProductV2[] | null>(() => (can("catalog.read_all") && editable ? api.fullCatalog() : Promise.resolve(null)), [ops, editable]);
  const model: DealerModel = dealer?.model ?? "agent";
  const priced = useMemo(
    () => (ops ? (fullCatalog.data && dealer ? pricedForDealer(fullCatalog.data, dealer) : null) : catalog.data),
    [ops, fullCatalog.data, dealer, catalog.data],
  );
  // What the line picker searches, or why there is nothing to search yet: Phoneware reads the whole record, a dealer its own view.
  const catalogRead = can("catalog.read_all") ? fullCatalog : catalog;
  const pickerSource: PickerSource = catalogRead.error
    ? { kind: "error", message: catalogRead.error.message, retrying: catalogRead.loading }
    : priced
      ? { kind: "ready", catalog: priced }
      : ops && !dealer && dealers.data
        ? { kind: "customer" }
        : { kind: "loading" };

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [sheet, setSheet] = useState<"send" | "pdf" | "saveas" | null>(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [from, setFrom] = useState<StartedFrom | null>(null);

  useTitle(quote ? quote.number : "New quote");

  useEffect(() => {
    if (quote) {
      setDraft(draftOf(quote));
      setDirty(false);
    }
  }, [quote]);

  // A quote started from a template opens once with the template's lines at today's prices for the dealer.
  // Lines the template can't quote now are left out and named, with the server's reason.
  const seeded = useRef(false);
  useEffect(() => {
    const t = template.data;
    if (seeded.current || !t || !priced) return;
    seeded.current = true;
    const start = startLines(t, priced);
    setDraft((d) => ({ ...d, term: t.term, pdfNotes: t.pdfNotes, lines: start.lines }));
    setFrom({ template: t, left: start.left, removed: false, busy: false, problem: null });
  }, [template.data, priced]);

  // Who signs comes only from the chosen customer's detail call, which reads its contacts live from Autotask.
  // Picking a customer asks for its signer once those contacts arrive; reopening a saved quote keeps the one it has.
  const chosenId = draft.customer?.id ?? null;
  const pickSigner = useRef(false);
  useEffect(() => {
    if (!editable) return;
    const wanted = chosenId ?? (id === null ? customerId : null);
    if (wanted === null) {
      setContacts([]);
      return;
    }
    let live = true;
    api.customer(wanted).then(
      (c) => {
        if (!live) return;
        setContacts(c.contacts);
        if (ops) setDraftDealerId(c.dealerId);
        if (chosenId === null || pickSigner.current) {
          pickSigner.current = false;
          const signer = signerOf(c.contacts);
          change({ customer: { id: c.autotaskCompanyId, name: c.name }, contactName: signer?.name ?? null, contactEmail: signer?.email ?? null });
        }
      },
      () => {
        if (live) setContacts([]);
      },
    );
    return () => {
      live = false;
    };
    // `change` only merges the patch it is given.
  }, [api, chosenId, customerId, editable, id, ops]);

  function change(patch: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  }

  function choose(c: CustomerSummary) {
    if (c.autotaskCompanyId === chosenId) return;
    if (ops) setDraftDealerId(c.dealerId);
    pickSigner.current = true;
    setContacts([]);
    change({ customer: { id: c.autotaskCompanyId, name: c.name }, contactName: null, contactEmail: null });
  }

  function add(p: PricedProduct) {
    const had = draft.lines.find((l) => l.productId === p.id)?.qty ?? 0;
    setDraft((d) => {
      const have = d.lines.find((l) => l.productId === p.id);
      const lines = have
        ? d.lines.map((l) => (l.productId === p.id ? { ...l, qty: l.qty + 1 } : l))
        : [
          ...d.lines,
          {
            productId: p.id,
            code: p.code,
            name: p.name,
            billing: p.billing,
            unit: p.unit,
            qty: 1,
            unitPrice: p.listPrice,
            listPrice: p.listPrice,
            floorPrice: p.floorPrice,
            buyPrice: p.buyPrice,
          },
        ];
      return { ...d, lines };
    });
    setDirty(true);
    toast(had ? `Added ${p.name}. ${had + 1} on the quote.` : `Added ${p.name}.`);
  }

  const totals = quoteTotals(draft.lines, model);
  const low = floorViolations(draft.lines, model);

  /** Takes the lines this quote left out off the template itself, against the version the quote started from. */
  async function dropLeft(): Promise<void> {
    if (!from) return;
    const t = from.template;
    const gone = new Set(from.left.map((l) => l.productId));
    setFrom({ ...from, busy: true, problem: null });
    try {
      const next = await api.updateQuoteTemplate(t.id, { lines: t.lines.filter((l) => !gone.has(l.productId)), expectedUpdatedAt: t.updatedAt });
      setFrom({ ...from, template: next, removed: true, busy: false, problem: null });
      toast(`Removed ${plural(from.left.length, "line")} from \u201c${t.name}\u201d.`);
    } catch (err) {
      // Someone saved the template after this quote started from it: show it, and let the next try go against what they saved.
      const fresh = err instanceof ApiError && err.status === 409 ? await api.quoteTemplate(t.id).catch(() => null) : null;
      if (fresh && fresh.updatedAt !== t.updatedAt) {
        setFrom({ ...from, template: fresh, busy: false, problem: `Someone else saved this template ${whenIn(fresh.updatedAt)}, after you opened it.` });
      } else {
        setFrom({ ...from, busy: false, problem: errorText(err) });
      }
    }
  }

  async function save(): Promise<void> {
    if (!draft.customer) return;
    const input: QuoteInput = {
      customerId: draft.customer.id,
      type: draft.type,
      term: draft.term,
      lines: draft.lines.map((l) => ({ productId: l.productId, qty: l.qty, unitPrice: l.unitPrice })),
      pdfNotes: draft.pdfNotes,
      internalNotes: draft.internalNotes,
      approvalNote: low.length ? draft.approvalNote.trim() || null : null,
      contactName: draft.contactName,
      contactEmail: draft.contactEmail,
    };
    setBusy(true);
    setProblem(null);
    try {
      const saved = quote ? await api.updateQuote(quote.id, input) : await api.createQuote(input);
      if (quote) {
        loaded.set(saved);
        toast(`Saved ${saved.number}.`);
      } else {
        toast(`Saved as ${saved.number}.`);
        navigate(`/quotes/${encodeURIComponent(saved.id)}`, { replace: true });
      }
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function clone(): Promise<void> {
    if (!quote) return;
    try {
      const copy = await api.cloneQuote(quote.id);
      toast(`Cloned ${quote.number} as ${copy.number}.`);
      navigate(`/quotes/${encodeURIComponent(copy.id)}`);
    } catch (err) {
      setProblem(errorText(err));
    }
  }

  async function decide(decision: "approved" | "sent_back"): Promise<void> {
    if (!quote) return;
    setBusy(true);
    setProblem(null);
    try {
      const next = await api.decideApproval(quote.id, { decision, note: decisionNote.trim() || null });
      loaded.set(next);
      toast(`${next.number} ${decision === "approved" ? "approved" : "sent back"}.`);
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (id && !quote) {
    if (isNotYours(loaded.error)) return <NotYours kind="quote" back="/quotes" />;
    return (
      <>
        <Crumb to="/quotes">Quotes</Crumb>
        {loaded.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {loaded.error.message}
          </Callout>
        ) : (
          <div className="empty">Loading the quote.</div>
        )}
      </>
    );
  }
  if (!seesPrices) return <NotYours kind="quote" back="/" />;

  const sendCheck = quote ? canSend(quote) : null;
  const sendReason = !quote ? "Save the quote first." : dirty ? "Save your changes first." : sendCheck && !sendCheck.ok ? sendCheck.reason : null;
  const repWord = quote ? (quote.repEmail === me.user.email ? me.user.name : quote.repEmail) : me.user.name;

  const actions = (
    <>
      {quote ? (
        <button className="btn" type="button" onClick={() => setSheet("pdf")}>
          <Icon name="file-text" size={17} />
          Preview PDF
        </button>
      ) : null}
      {quote && writesHere && !mac ? (
        <button className="btn" type="button" onClick={() => void clone()}>
          <Icon name="copy" size={17} />
          Clone
        </button>
      ) : null}
      {editable ? (
        <button
          className="btn"
          type="button"
          disabled={draft.lines.length === 0}
          title={draft.lines.length === 0 ? "Add a line first." : undefined}
          data-testid="quote-save-template"
          onClick={() => setSheet("saveas")}
        >
          <Icon name="layout-template" size={16} />
          Save as template
        </button>
      ) : null}
      {editable ? (
        <button className="btn" type="button" disabled={busy || !draft.customer || (!dirty && quote !== null)} data-testid="quote-save" onClick={() => void save()}>
          <Icon name="check" size={15} />
          {busy ? "Saving" : "Save"}
        </button>
      ) : null}
      {writesHere && (editable || quote?.status === "sent" || quote?.status === "viewed") ? (
        <button
          className="btn primary"
          type="button"
          disabled={sendReason !== null}
          title={sendReason ?? undefined}
          data-testid="quote-send"
          onClick={() => setSheet("send")}
        >
          <Icon name="send" size={15} />
          {quote?.link ? "Resend QuoteLink" : "Send QuoteLink"}
        </button>
      ) : null}
    </>
  );

  const title = (
    <>
      {draft.customer?.name ?? "New quote"}
      {quote ? <QuoteStatusChip status={quote.status} testId="quote-status" /> : null}
      {viewOnly ? (
        <Chip kind="quiet" icon="eye" testId="quote-view-only">
          View only
        </Chip>
      ) : null}
      {quote ? <MadeBy via={quote.createdVia} person={repWord} testId="quote-made-by" /> : null}
    </>
  );
  const sub = quote ? (
    <>
      <span data-testid="quote-number">{quote.number}</span> · {QUOTE_TYPE[quote.type]} · {formatTerm(quote.term)} · {repWord}
      {ops && dealer ? ` \u00b7 ${dealer.name}` : ""}
    </>
  ) : ops && dealer ? (
    `New quote for ${dealer.name}. Omni numbers it when you save.`
  ) : (
    "Pick the customer, add products from the catalog, then save. Omni numbers the quote when you save it."
  );

  return (
    <>
      <Crumb to="/quotes">Quotes</Crumb>
      <Head title={title} sub={sub} actions={actions} />
      {/* M8 (#1386): a draft an AI or app made says so before anyone sends it. */}
      {quote ? <MadeByCallout quote={quote} person={repWord} /> : null}
      <div className="builder">
        <div className="builder-main">
          {from ? <StartedFromNotice from={from} onRemove={() => void dropLeft()} /> : null}
          {quote ? <IntakeCreatedNotice quote={quote} /> : null}
          {template.error ? (
            <Callout kind="stop" icon="triangle-alert">
              The template did not load: {template.error.message}
            </Callout>
          ) : null}
          {problem ? (
            <Callout kind="stop" icon="triangle-alert">
              {problem}
            </Callout>
          ) : null}
          {mac ? (
            <div data-testid="quote-mac">
              <Callout icon="lock">
                A MAC quote{quote?.source ? ` from ticket ${quote.source.ticketNumber}` : ""}. Phoneware makes and sells MAC orders from New MAC order on Orders.
              </Callout>
            </div>
          ) : viewOnly ? (
            <ViewOnly testId="quote-view-only-note">
              {quoteScope === "house"
                ? `This is ${dealer ? `a ${dealer.name}` : "another dealer\u2019s"} quote. Sales quotes for Phoneware\u2019s own customers.`
                : `${roleLabel(me.user.role)} sees every dealer\u2019s quotes and changes none of them.`}
            </ViewOnly>
          ) : !editable && writesHere && quote ? (
            <Callout icon="lock">
              {quote.status === "lost" ? "This quote is closed." : "This quote has gone to the customer, so it is locked."} Clone it to start a new version.
            </Callout>
          ) : null}
          <section className="card">
            <div className="card-h">
              <h2>Customer and terms</h2>
              <span className="note">Customers come from Autotask</span>
            </div>
            {editable ? (
              <div className="qfields">
                <div className="field">
                  <span>Customer</span>
                  <CustomerPicker chosen={draft.customer} onChoose={choose} dealerId={ops ? (dealerId ?? (quoteScope === "house" ? (house?.id ?? "") : null)) : null} />
                  {ops && quoteScope === "house" ? (
                    <span className="field-note" data-testid="quote-house-only">
                      Sales quotes for Phoneware&rsquo;s own customers. Another dealer&rsquo;s customer opens view only.
                    </span>
                  ) : null}
                </div>
                <label className="field">
                  <span>Who signs</span>
                  <select
                    className="input"
                    value={draft.contactEmail ?? ""}
                    disabled={contacts.length === 0}
                    onChange={(e) => {
                      const c = contacts.find((x) => x.email === e.target.value);
                      change({ contactName: c?.name ?? null, contactEmail: c?.email ?? null });
                    }}
                  >
                    {contacts.length === 0 ? <option value="">Pick a customer first</option> : null}
                    {contacts
                      .filter((c) => c.email)
                      .map((c) => (
                        <option key={c.autotaskContactId} value={c.email ?? ""}>
                          {c.name}, {c.email}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="field">
                  <span>Contract term</span>
                  <select
                    className="input"
                    value={String(draft.term)}
                    onChange={(e) => change({ term: TERMS.find((t) => String(t) === e.target.value) ?? 36 })}
                  >
                    {TERMS.map((t) => (
                      <option key={String(t)} value={String(t)}>
                        {formatTerm(t)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="field">
                  <span id="quote-type-label">Type</span>
                  <div className="seg" role="group" aria-labelledby="quote-type-label">
                    {PICKER_TYPES.map((t) => (
                      <button key={t} type="button" aria-pressed={draft.type === t} onClick={() => change({ type: t })}>
                        {QUOTE_TYPE[t]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <dl className="facts">
                <div>
                  <dt>Customer</dt>
                  <dd>{quote ? <Link to={`/customers/${quote.customerId}`}>{quote.customerName}</Link> : null}</dd>
                </div>
                <div>
                  <dt>Who signs</dt>
                  <dd>{draft.contactName ? `${draft.contactName}${draft.contactEmail ? `, ${draft.contactEmail}` : ""}` : "Not set"}</dd>
                </div>
                <div>
                  <dt>Contract term</dt>
                  <dd>{formatTerm(draft.term)}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{QUOTE_TYPE[draft.type]}</dd>
                </div>
                {ops && dealer ? (
                  <div>
                    <dt>Dealer</dt>
                    <dd>
                      {dealer.name} <span className="tag">{dealer.model === "agent" ? "Dealer" : "Reseller"}</span>
                    </dd>
                  </div>
                ) : null}
              </dl>
            )}
          </section>
          {(["monthly", "one_time"] as Billing[]).map((b) => (
            <Lines
              key={b}
              billing={b}
              lines={draft.lines}
              model={model}
              editable={editable}
              ops={ops}
              onLine={(productId, patch) => change({ lines: draft.lines.map((l) => (l.productId === productId ? { ...l, ...patch } : l)) })}
              onRemove={(productId) => change({ lines: draft.lines.filter((l) => l.productId !== productId) })}
            />
          ))}
          {editable ? (
            <QuoteLinePicker
              source={pickerSource}
              model={model}
              dealerName={ops ? (dealer?.name ?? null) : null}
              showBlocked={ops && can("catalog.read_all")}
              lines={draft.lines}
              onAdd={add}
              onRetry={catalogRead.reload}
            />
          ) : null}
          <section className="card">
            <div className="card-h">
              <h2>Notes</h2>
            </div>
            <div className="notes">
              <label className="field">
                <span>On the PDF</span>
                <textarea
                  className="input"
                  placeholder="What the customer reads above the signature."
                  readOnly={!editable}
                  value={draft.pdfNotes}
                  onChange={(e) => change({ pdfNotes: e.target.value })}
                />
              </label>
              <label className="field">
                <span>
                  Internal <Icon name="lock" size={11} />
                </span>
                <textarea
                  className="input"
                  placeholder="Only your dealer and Phoneware see this."
                  readOnly={!editable}
                  value={draft.internalNotes}
                  onChange={(e) => change({ internalNotes: e.target.value })}
                />
              </label>
            </div>
          </section>
        </div>
        <aside className="builder-side">
          <section className="card totals">
            <div className="card-h">
              <h2>Totals</h2>
            </div>
            <dl className="sum">
              <div>
                <dt>Monthly</dt>
                <dd data-testid="quote-total-monthly">{formatCents(totals.monthly)}</dd>
              </div>
              <div>
                <dt>One-time</dt>
                <dd>{formatCents(totals.oneTime)}</dd>
              </div>
              <div className="est">
                <dt>Taxes and surcharges</dt>
                <dd>Not on the quote</dd>
              </div>
            </dl>
            <p className="margin">{TAX_NOTE_PHASE_1}</p>
            {totals.monthlyUnderList ? <p className="margin">{formatCents(totals.monthlyUnderList)} a month under list price.</p> : null}
            {totals.monthlyMargin !== null && !ops ? (
              <p className="margin">
                Your margin: <b>{formatCents(totals.monthlyMargin)}</b> a month.
              </p>
            ) : null}
            <dl className="sum">
              <div className="strong due">
                <dt>Due at signing</dt>
                <dd data-testid="quote-total-due">{formatCents(totals.dueAtSigning)}</dd>
              </div>
            </dl>
          </section>

          {ops && quote?.status === "needs_approval" ? (
            <section className="card approval">
              <div className="card-h">
                <h2>Approval</h2>
                <Chip kind="warn" icon="triangle-alert">
                  Below the floor
                </Chip>
              </div>
              <ul className="low-list">
                {quote.approval.reasons.map((r) => (
                  <li key={r.productId}>
                    {r.name} at {formatCents(r.unitPrice)}, floor {formatCents(r.floorPrice)}
                  </li>
                ))}
              </ul>
              <p className="reason">
                <b>{repWord}:</b> {quote.approval.requestNote || "No reason given."}
              </p>
              {can("approvals.decide") ? (
                <>
                  <label className="field">
                    <span>Note back to the dealer</span>
                    <textarea className="input" placeholder="Optional. They see this on the quote." value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
                  </label>
                  <div className="btnrow">
                    <button className="btn primary" type="button" disabled={busy} onClick={() => void decide("approved")}>
                      <Icon name="check" size={15} />
                      Approve
                    </button>
                    <button className="btn" type="button" disabled={busy} onClick={() => void decide("sent_back")}>
                      <Icon name="undo-2" size={15} />
                      Send back
                    </button>
                  </div>
                </>
              ) : (
                <DecidedBy testId="quote-approval-readonly">Billing ops or a Super admin decides this one.</DecidedBy>
              )}
            </section>
          ) : null}

          {editable && low.length > 0 ? (
            <section className="card approval">
              <div className="card-h">
                <h2>Needs approval</h2>
                <Chip kind="warn" icon="triangle-alert">
                  {quote?.status === "needs_approval" && !dirty ? "With Phoneware" : "Below the floor"}
                </Chip>
              </div>
              <p>
                {low.length === 1 ? "One line is" : `${low.length} lines are`} below the floor, so Phoneware approves this before it goes to the
                customer.
              </p>
              <ul className="low-list" data-testid="quote-floor-warning">
                {low.map((r) => (
                  <li key={r.productId}>
                    {r.name} at {formatCents(r.unitPrice)}, floor {formatCents(r.floorPrice)}
                  </li>
                ))}
              </ul>
              <label className="field">
                <span>Why this price?</span>
                <textarea
                  className="input"
                  data-testid="quote-approval-note"
                  placeholder="What Phoneware should know, like the competing offer or the size of the deal."
                  readOnly={!editable}
                  value={draft.approvalNote}
                  onChange={(e) => change({ approvalNote: e.target.value })}
                />
              </label>
              <p className="card-note">Phoneware sees it in their approvals queue when you save.</p>
            </section>
          ) : null}

          {quote && quote.status === "draft" && quote.approval.decision === "approved" ? (
            <Callout kind="ok" icon="badge-check">
              Phoneware approved the price{quote.approval.decisionNote ? `: ${quote.approval.decisionNote}` : "."} Ready to send.
            </Callout>
          ) : null}
          {quote && quote.status === "draft" && quote.approval.decision === "sent_back" ? (
            <Callout kind="warn" icon="undo-2">
              Phoneware sent this back{quote.approval.decisionNote ? `: ${quote.approval.decisionNote}` : "."}
            </Callout>
          ) : null}

          {quote?.link ? (
            <section className="card">
              <div className="card-h">
                <h2>QuoteLink</h2>
                <span className="note">
                  {quote.link.viewedAt ? `Opened ${whenIn(quote.link.viewedAt)}` : "Not opened yet"}
                </span>
              </div>
              <div className="linkbox">
                <code data-testid="quote-link-url">{quoteLinkUrl(quote.link.token) ?? ""}</code>
              </div>
              <p className="card-note">
                Sent to {quote.link.sentTo} {whenIn(quote.link.sentAt)}. It works until{" "}
                {new Date(quote.link.expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.
              </p>
            </section>
          ) : null}

          {quote?.orderId ? (
            <Link className="callout orderlink" to={`/orders/${encodeURIComponent(quote.orderId)}`} data-testid="quote-order-link">
              <Icon name="package" size={16} />
              <span>{mac ? "Sold" : "Signed"}. Follow the order from here.</span>
              <Icon name="chevron-right" size={16} />
            </Link>
          ) : null}
          {quote ? <RecordActivity kind="quote" id={quote.id} version={quote.updatedAt} /> : null}
        </aside>
      </div>
      {sheet === "send" && quote ? (
        <SendSheet quote={quote} dealerName={dealer?.name ?? "your dealer"} onSent={(q) => loaded.set(q)} onClose={() => setSheet(null)} />
      ) : null}
      {sheet === "pdf" && quote ? <PdfSheet quote={quote} onClose={() => setSheet(null)} /> : null}
      {sheet === "saveas" ? (
        <SaveAsTemplateSheet
          dealer={dealer}
          ops={ops}
          lines={draft.lines}
          term={draft.term}
          pdfNotes={draft.pdfNotes}
          customerName={draft.customer?.name ?? null}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </>
  );
}
